import React, { forwardRef, useCallback } from 'react'
import {
  Card,
  Text,
  Stack,
  Box,
  Heading,
  Badge,
  Flex,
  Label,
  Button,
  TextArea,
} from '@sanity/ui'
import { ObjectInputProps, set, unset, useFormValue } from 'sanity'
import { TrashIcon, AddIcon, EditIcon } from '@sanity/icons'

interface SectionData {
  content?: string
  lastGenerated?: string
  rulesVersion?: string
  contextHash?: string
}

interface AIGeneratedContent {
  overview?: SectionData
  performanceAnalysis?: SectionData
  easeOfFitment?: SectionData
  tireAvailability?: SectionData
}

interface ContentOverrides {
  overview?: string
  performanceAnalysis?: string
  easeOfFitment?: string
  tireAvailability?: string
}

type SectionKey = 'overview' | 'performanceAnalysis' | 'easeOfFitment' | 'tireAvailability'

const AIContentWithOverrideInput = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    const { value, onChange, schemaType, path } = props

    const typedValue = value as ContentOverrides | undefined

    // Access the sibling aiGeneratedContent field
    // Build the path to aiGeneratedContent from current path
    const parentPath = path.slice(0, -1) // Remove 'contentOverrides' from path
    const aiContentPath = [...parentPath, 'aiGeneratedContent']
    const aiGeneratedContent = useFormValue(aiContentPath) as AIGeneratedContent | undefined

    // Section configuration
    const sections: { key: SectionKey; title: string; icon: string; disabled?: boolean }[] = [
      { key: 'overview', title: 'Overview', icon: '📝' },
      { key: 'performanceAnalysis', title: 'Performance Analysis', icon: '⚡', disabled: true },
      { key: 'easeOfFitment', title: 'Ease of Fitment', icon: '🔧', disabled: true },
      { key: 'tireAvailability', title: 'Tire Availability', icon: '🛞', disabled: true },
    ]

    // Helper: Format date for display
    const formatDate = (dateString?: string) => {
      if (!dateString) return 'Unknown'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    // Handle override change
    const handleOverrideChange = useCallback(
      (sectionKey: SectionKey, newValue: string) => {
        const currentValue = typedValue || {}
        if (newValue && newValue.trim() !== '') {
          onChange(set({ ...currentValue, [sectionKey]: newValue }))
        } else {
          // If empty, remove the key
          const { [sectionKey]: _, ...rest } = currentValue
          if (Object.keys(rest).length === 0) {
            onChange(unset())
          } else {
            onChange(set(rest))
          }
        }
      },
      [onChange, typedValue],
    )

    // Handle remove override
    const handleRemoveOverride = useCallback(
      (sectionKey: SectionKey) => {
        if (!typedValue) return
        const { [sectionKey]: _, ...rest } = typedValue
        if (Object.keys(rest).length === 0) {
          onChange(unset())
        } else {
          onChange(set(rest))
        }
      },
      [onChange, typedValue],
    )

    // Render individual section
    const renderSection = (section: (typeof sections)[0]) => {
      const aiData = aiGeneratedContent?.[section.key]
      const hasAIContent = aiData?.content && aiData.content.trim() !== ''
      const overrideValue = typedValue?.[section.key]
      const hasOverride = overrideValue && overrideValue.trim() !== ''
      const [isEditing, setIsEditing] = React.useState(hasOverride)

      return (
        <Card
          key={section.key}
          padding={4}
          radius={2}
          shadow={1}
          tone={section.disabled ? 'transparent' : hasAIContent ? 'default' : 'transparent'}
          border
          style={section.disabled ? { opacity: 0.6 } : undefined}
        >
          <Stack gap={4}>
            {/* Disabled banner */}
            {section.disabled && (
              <Card tone="caution" padding={2} radius={2}>
                <Text size={1} muted>
                  This section is currently hidden on the frontend. Data is still saved and generated.
                </Text>
              </Card>
            )}

            {/* Header */}
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={2}>
                <Text size={2}>{section.icon}</Text>
                <Heading size={1}>{section.title}</Heading>
              </Flex>
              {hasOverride ? (
                <Badge tone="primary">
                  <Flex align="center" gap={1}>
                    <EditIcon />
                    <span>Overridden</span>
                  </Flex>
                </Badge>
              ) : hasAIContent ? (
                <Badge tone="positive">
                  AI Generated
                </Badge>
              ) : (
                <Badge tone="default">
                  Not Generated
                </Badge>
              )}
            </Flex>

            {/* AI Content Display */}
            {hasAIContent && (
              <Box>
                <Label
                  size={0}
                  muted={!!hasOverride}
                  style={{ marginBottom: '8px', display: 'block' }}
                >
                  {hasOverride ? 'AI Content (not in use):' : 'AI Generated Content:'}
                </Label>
                <Box
                  padding={3}
                  style={{
                    backgroundColor: hasOverride
                      ? 'var(--card-muted-bg-color)'
                      : 'var(--card-bg-color)',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    maxHeight: hasOverride ? '100px' : '200px',
                    overflowY: 'auto',
                    opacity: hasOverride ? 0.5 : 1,
                    border: '1px solid var(--card-border-color)',
                  }}
                >
                  <Text size={1} muted={!!hasOverride}>
                    {aiData.content}
                  </Text>
                </Box>
                {!hasOverride && aiData.lastGenerated && (
                  <Flex align="center" gap={2} style={{ marginTop: '8px' }}>
                    <Text size={0} muted weight="semibold">
                      Generated:
                    </Text>
                    <Text size={0} muted>
                      {formatDate(aiData.lastGenerated)}
                    </Text>
                  </Flex>
                )}
              </Box>
            )}

            {/* No AI Content Warning */}
            {!hasAIContent && !hasOverride && (
              <Card tone="transparent" padding={3} border radius={2}>
                <Text size={1} muted>
                  No AI content generated yet. You can add a manual override below.
                </Text>
              </Card>
            )}

            {/* Override Section */}
            <Box>
              {hasOverride || isEditing ? (
                <Stack gap={3}>
                  <Flex align="center" justify="space-between">
                    <Label size={0}>
                      Manual Override {hasOverride ? '(Active)' : ''}:
                    </Label>
                    <Button
                      mode="ghost"
                      tone="critical"
                      fontSize={1}
                      padding={2}
                      icon={TrashIcon}
                      text="Remove Override"
                      onClick={() => {
                        handleRemoveOverride(section.key)
                        setIsEditing(false)
                      }}
                    />
                  </Flex>
                  <TextArea
                    value={overrideValue || ''}
                    onChange={(e) => handleOverrideChange(section.key, e.currentTarget.value)}
                    rows={4}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      lineHeight: '1.6',
                    }}
                    placeholder="Enter your custom content here..."
                  />
                  <Text size={0} muted>
                    This content will be used on the frontend instead of the AI-generated
                    content.
                  </Text>
                </Stack>
              ) : (
                <Button
                  mode="ghost"
                  tone="default"
                  fontSize={1}
                  padding={3}
                  icon={AddIcon}
                  text="Add Manual Override"
                  onClick={() => setIsEditing(true)}
                  style={{ width: '100%' }}
                />
              )}
            </Box>
          </Stack>
        </Card>
      )
    }

    // Main render
    return (
      <Stack gap={4} ref={ref}>
        {/* Title */}
        <Box>
          <Label size={1}>{schemaType.title}</Label>
          {schemaType.description && (
            <Box paddingTop={2}>
              <Text size={1} muted>
                {schemaType.description}
              </Text>
            </Box>
          )}
        </Box>

        {/* Info Card */}
        <Card padding={3} tone="primary" radius={2}>
          <Flex align="center" gap={2}>
            <Text size={1}>💡</Text>
            <Text size={1}>
              Add manual overrides to customize AI-generated content. Overrides take priority on
              the frontend.
            </Text>
          </Flex>
        </Card>

        {/* Sections */}
        {sections.map(renderSection)}
      </Stack>
    )
  },
)

AIContentWithOverrideInput.displayName = 'AIContentWithOverrideInput'

export default AIContentWithOverrideInput
